/* eslint-disable array-callback-return */
import React, { useState } from "react";
import { observer } from "mobx-react";
import { observable, action } from "mobx";
import DialerServiceData from "Stores/DialerServiceStore";
import userDetail from "Stores/userDetail";
import { transactionAnimationState } from "Components/common/TransactionAnimation";
import { toasterState } from "Components/common/toaster";

class VoBizBuyNumberPopupState {
  @observable showPopup = false;
  @observable callBack = () => {};

  @action
  setShowPopup = val => {
    this.showPopup = val;
    if (val) {
      document.getElementsByTagName("body")[0].classList.add("scrollHidden");
    } else {
      document.getElementsByTagName("body")[0].classList.remove("scrollHidden");
    }
  };

  @action
  setCallBack = val => {
    this.callBack = val;
  };
}

const voBizBuyNumberPopupState = new VoBizBuyNumberPopupState();

const NUMBER_SERIES = [
  { prefix: "80", label: "80 series", example: "+91 80XXX XXXXX", price: "$8/mo" },
  { prefix: "79", label: "79 series", example: "+91 79XXX XXXXX", price: "$10/mo" },
  { prefix: "92", label: "92 series", example: "+91 92XXX XXXXX", price: "$12/mo" }
];

// Step 1: Choose series
const SeriesStep = ({ selectedPrefix, setSelectedPrefix, onCancel, onNext, loadingNumbers }) => (
  <>
    <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "16px" }}>
      Choose a number series:
    </p>
    <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "24px" }}>
      {NUMBER_SERIES.map(series => (
        <label
          key={series.prefix}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 14px",
            border: `2px solid ${selectedPrefix === series.prefix ? "#3953fb" : "#e5e7eb"}`,
            borderRadius: "8px",
            cursor: "pointer",
            backgroundColor: selectedPrefix === series.prefix ? "#f0f3ff" : "#fff",
            transition: "all 0.15s"
          }}
          onClick={() => setSelectedPrefix(series.prefix)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <input
              type="radio"
              name="numberSeries"
              value={series.prefix}
              checked={selectedPrefix === series.prefix}
              onChange={() => setSelectedPrefix(series.prefix)}
              style={{ accentColor: "#3953fb", width: "16px", height: "16px", flexShrink: 0 }}
            />
            <div>
              <div style={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>
                {series.label}
              </div>
              <div style={{ fontSize: "12px", color: "#6b7280" }}>{series.example}</div>
            </div>
          </div>
          <span style={{
            fontSize: "13px",
            fontWeight: 600,
            color: selectedPrefix === series.prefix ? "#3953fb" : "#6b7280"
          }}>
            {series.price}
          </span>
        </label>
      ))}
    </div>
    <div className="actionBtn">
      <div className="btnCont">
        <div className="backCont btn" onClick={onCancel}>Cancel</div>
        <div
          className={`purchaseBtn btn ${loadingNumbers ? "disabled" : ""}`}
          onClick={!loadingNumbers ? onNext : undefined}
        >
          {loadingNumbers ? "Loading..." : "View Numbers"}
        </div>
      </div>
    </div>
  </>
);

// Step 2: Pick a number from the list
const NumberListStep = ({ numbers, selectedNumber, setSelectedNumber, onBack, onBuy, purchasing, seriesInfo }) => (
  <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
    {/* Sub-header */}
    <div style={{ marginBottom: "10px", flexShrink: 0 }}>
      <span style={{ fontSize: "13px", color: "#6b7280" }}>
        Available numbers for{" "}
        <strong style={{ color: "#374151" }}>{seriesInfo?.label}</strong>
        {" "}— {seriesInfo?.price}
      </span>
    </div>

    {/* Scrollable number list — takes remaining space */}
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        paddingRight: "4px",
        minHeight: 0
      }}
    >
      {numbers.length === 0 ? (
        <div style={{ textAlign: "center", color: "#9ca3af", fontSize: "13px", padding: "24px 0" }}>
          No numbers available for this series right now.
        </div>
      ) : (
        numbers.map((num, idx) => {
          const display = num.number || num;
          const isSelected = selectedNumber === display;
          return (
            <label
              key={idx}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "10px 14px",
                border: `2px solid ${isSelected ? "#3953fb" : "#e5e7eb"}`,
                borderRadius: "8px",
                cursor: "pointer",
                backgroundColor: isSelected ? "#f0f3ff" : "#fff",
                transition: "all 0.15s",
                flexShrink: 0
              }}
              onClick={() => setSelectedNumber(display)}
            >
              <input
                type="radio"
                name="phoneNumber"
                value={display}
                checked={isSelected}
                onChange={() => setSelectedNumber(display)}
                style={{ accentColor: "#3953fb", width: "16px", height: "16px", flexShrink: 0 }}
              />
              <span style={{ fontSize: "14px", color: "#374151", fontWeight: isSelected ? 600 : 400 }}>
                {display}
              </span>
            </label>
          );
        })
      )}
    </div>

    {/* Action buttons — always at bottom */}
    <div style={{ flexShrink: 0, paddingTop: "16px", borderTop: "1px solid #f3f4f6", marginTop: "12px" }}>
      <div style={{ display: "flex", gap: "10px", marginBottom: "8px" }}>
        <div className="backCont btn" style={{ flex: 1 }} onClick={onBack}>← Back</div>
        <div
          className={`purchaseBtn btn ${purchasing || !selectedNumber || numbers.length === 0 ? "disabled" : ""}`}
          style={{ flex: 2 }}
          onClick={!purchasing && selectedNumber && numbers.length > 0 ? onBuy : undefined}
        >
          {purchasing ? "Purchasing..." : "Buy Number"}
        </div>
      </div>
      <span className="chargeText">
        The selected number will be activated immediately after purchase.
      </span>
    </div>
  </div>
);

const VoBizBuyNumberPopup = observer(() => {
  const [step, setStep] = useState(1);
  const [selectedPrefix, setSelectedPrefix] = useState("80");
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState("");
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  const teamId = userDetail?.userInfo?.team_id || userDetail?.userFeatureFlag?.id || "";
  const seriesInfo = NUMBER_SERIES.find(s => s.prefix === selectedPrefix);

  const handleViewNumbers = async () => {
    setLoadingNumbers(true);
    try {
      const results = await DialerServiceData.getAvailableIndiaNumbers(selectedPrefix);
      setAvailableNumbers(results || []);
      setSelectedNumber(results?.[0]?.number || results?.[0] || "");
      setStep(2);
    } catch (e) {
      toasterState.setToastMsg("Failed to load available numbers. Please try again.", "failure");
    } finally {
      setLoadingNumbers(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setAvailableNumbers([]);
    setSelectedNumber("");
  };

  const handlePurchase = async () => {
    if (!selectedNumber || !teamId) return;
    setPurchasing(true);
    transactionAnimationState.setShowPopup(true);
    try {
      const success = await DialerServiceData.purchaseIndiaNumber(selectedNumber);
      if (success) {
        closePopup();
        voBizBuyNumberPopupState.callBack && voBizBuyNumberPopupState.callBack(selectedNumber);
      }
    } catch (e) {
      toasterState.setToastMsg("Failed to purchase number. Please try again.", "failure");
    } finally {
      transactionAnimationState.setShowPopup(false);
      setPurchasing(false);
    }
  };

  const closePopup = () => {
    setStep(1);
    setSelectedPrefix("80");
    setAvailableNumbers([]);
    setSelectedNumber("");
    setPurchasing(false);
    setLoadingNumbers(false);
    voBizBuyNumberPopupState.setShowPopup(false);
  };

  if (!voBizBuyNumberPopupState.showPopup) return null;

  return (
    <div className="buyNewNumberPopup popupWrapper">
      <div className="confirmation-popup common-popup">
        <div className="newNumberPopupWrapper popup-wrap animated customzoomIn">
          <div className="newNumberCont">
            <div className="popupHeader">
              <div className="popupTitle">Buy India Phone Number</div>
              <div className="popup-close" title="Close" onClick={closePopup}>
                <i className="material-icons">close</i>
              </div>
            </div>
            <div className="popupBody" style={{ display: "flex", flexDirection: "column", height: step === 2 ? "360px" : "auto", overflow: "hidden" }}>
              {step === 1 ? (
                <SeriesStep
                  selectedPrefix={selectedPrefix}
                  setSelectedPrefix={setSelectedPrefix}
                  onCancel={closePopup}
                  onNext={handleViewNumbers}
                  loadingNumbers={loadingNumbers}
                />
              ) : (
                <NumberListStep
                  numbers={availableNumbers}
                  selectedNumber={selectedNumber}
                  setSelectedNumber={setSelectedNumber}
                  onBack={handleBack}
                  onBuy={handlePurchase}
                  purchasing={purchasing}
                  seriesInfo={seriesInfo}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="common-popup-mask" />
    </div>
  );
});

export default VoBizBuyNumberPopup;
export { VoBizBuyNumberPopup, voBizBuyNumberPopupState };
